import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, Calendar, Paperclip, MapPin, Clock, DollarSign, Star } from "lucide-react";
import { Activity } from "@/hooks/useTrips";
import { EditActivityDialog } from "../dialogs/EditActivityDialog";
import { DeleteItemDialog } from "../dialogs/DeleteItemDialog";
import { AttachmentsSheet, AttachmentsBadge, SourceEmailBadge } from "../attachments";
import { getCompanyLogoUrl } from "@/lib/companyLogos";
import { safeFormat } from "@/lib/formatDateTime";

interface ActivityCardProps {
  activity: Activity;
  tripId?: string;
  index?: number;
  readOnly?: boolean;
}

export const ActivityCard = ({ activity, tripId, index, readOnly = false }: ActivityCardProps) => {
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return "N/A";
    const result = safeFormat(dateStr, "EEE, MMM d, yyyy");
    return result === "—" ? dateStr : result;
  };

  // Get activity/company logo URL - prefer operator, fall back to activity name
  // This works for known venues like Disney, Universal, Viator, etc.
  const logoUrl = getCompanyLogoUrl(activity.operator, 64) || getCompanyLogoUrl(activity.name, 64);

  // Check if there are expandable details to show
  const hasExpandableDetails = !!(
    activity.operator ||
    activity.meeting_point ||
    activity.address ||
    (activity.included && activity.included.length > 0) ||
    (activity.not_included && activity.not_included.length > 0) ||
    (activity.highlights && activity.highlights.length > 0)
  );

  return (
    <>
      <Card variant="interactive">
        <CardContent className="p-6">
          {/* Header with activity info and actions */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-center gap-3">
                  {logoUrl ? (
                    <div className="w-12 h-12 rounded bg-green-50 border border-green-200 flex items-center justify-center overflow-hidden">
                      <img
                        src={logoUrl}
                        alt={activity.name}
                        className="w-8 h-8 object-contain mix-blend-multiply"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const parent = e.currentTarget.parentElement;
                          if (parent) {
                            parent.innerHTML = `<span class="text-xs font-bold text-green-600">${activity.name?.substring(0, 2).toUpperCase() || 'AC'}</span>`;
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded bg-green-50 border border-green-200 flex items-center justify-center">
                      <span className="text-xs font-bold text-green-600">{activity.name?.substring(0, 2).toUpperCase() || 'AC'}</span>
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold text-lg mb-1">{activity.name}</h3>
                    {activity.location && (
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {activity.location}
                      </p>
                    )}
                  </div>
                </div>
                {!readOnly && tripId !== undefined && index !== undefined && (
                  <div className="flex items-center gap-2">
                    <AttachmentsBadge
                      tripId={tripId}
                      itemType="activities"
                      itemIndex={index}
                      onClick={() => setShowAttachments(true)}
                    />
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {activity.source_email_id && (
                        <SourceEmailBadge onClick={() => setShowAttachments(true)} />
                      )}
                      <Button variant="ghost" size="icon" onClick={() => setShowAttachments(true)} title="Attachments">
                        <Paperclip className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setShowEdit(true)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setShowDelete(true)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Time and Duration */}
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-4">
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {formatDate(activity.date)}
            </div>
            {activity.time && (
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {activity.time}
              </div>
            )}
            {activity.duration && (
              <div className="flex items-center gap-1">
                <span className="font-medium">Duration:</span>
                {activity.duration}
              </div>
            )}
            {activity.confirmation_number && (
              <div>
                <span className="font-medium">Conf:</span> {activity.confirmation_number}
              </div>
            )}
          </div>

          {/* Description */}
          {activity.notes && (
            <p className="text-sm text-muted-foreground mb-4">{activity.notes}</p>
          )}

          {/* Cost */}
          {activity.cost && (
            <div className="bg-gradient-to-r from-green-50 to-green-50/50 rounded-lg p-3 mb-4 border border-green-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-green-900">Activity Cost</span>
                <span className="text-xl font-bold text-green-700 flex items-center gap-1">
                  <DollarSign className="w-5 h-5" />
                  {activity.cost.toLocaleString()} {activity.currency || 'USD'}
                </span>
              </div>
            </div>
          )}

          {/* Expandable Details */}
          {expanded && (
            <div className="border-t border-border pt-4 space-y-3">
              {activity.operator && (
                <div>
                  <span className="text-sm font-medium">Operator:</span>
                  <span className="text-sm text-muted-foreground ml-2">{activity.operator}</span>
                </div>
              )}
              {activity.meeting_point && (
                <div>
                  <span className="text-sm font-medium">Meeting Point:</span>
                  <p className="text-sm text-muted-foreground mt-1">{activity.meeting_point}</p>
                </div>
              )}
              {activity.address && (
                <div>
                  <span className="text-sm font-medium">Address:</span>
                  <p className="text-sm text-muted-foreground mt-1">{activity.address}</p>
                </div>
              )}
              {activity.included && activity.included.length > 0 && (
                <div>
                  <span className="text-sm font-medium">What's Included:</span>
                  <ul className="mt-2 space-y-1">
                    {activity.included.map((item, idx) => (
                      <li key={idx} className="text-sm text-muted-foreground ml-2 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {activity.not_included && activity.not_included.length > 0 && (
                <div>
                  <span className="text-sm font-medium">Not Included:</span>
                  <ul className="mt-2 space-y-1">
                    {activity.not_included.map((item, idx) => (
                      <li key={idx} className="text-sm text-muted-foreground ml-2 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-400"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {activity.highlights && activity.highlights.length > 0 && (
                <div>
                  <span className="text-sm font-medium">Highlights:</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {activity.highlights.map((highlight, idx) => (
                      <span key={idx} className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs flex items-center gap-1">
                        <Star className="w-3 h-3" />
                        {highlight}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Toggle Button - only show if there are details to expand */}
          {hasExpandableDetails && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-full mt-4 text-sm text-accent hover:text-accent/80 transition-colors"
            >
              {expanded ? 'Show less' : 'Show more details'}
            </button>
          )}
        </CardContent>
      </Card>

      {!readOnly && tripId !== undefined && index !== undefined && (
        <>
          <EditActivityDialog
            activity={activity}
            tripId={tripId}
            index={index}
            open={showEdit}
            onOpenChange={setShowEdit}
          />
          <DeleteItemDialog
            tripId={tripId}
            itemType="activities"
            index={index}
            itemName={activity.name}
            open={showDelete}
            onOpenChange={setShowDelete}
          />
          <AttachmentsSheet
            open={showAttachments}
            onOpenChange={setShowAttachments}
            tripId={tripId}
            itemType="activities"
            itemIndex={index}
            itemTitle={activity.name}
            sourceEmailId={activity.source_email_id}
          />
        </>
      )}
    </>
  );
};
